import { useParams, useNavigate, Link } from "react-router-dom";
import { Check, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProductMockup from "@/components/marketing/ProductMockup";
import { ExitIntent } from "@/components/marketing/Conversion";
import { track } from "@/lib/api";
import { useSEO, breadcrumbSchema } from "@/lib/seo";
import { LANDING_PAGES, LP_PAGES, MODULES, MODULE_LINKS } from "@/lib/content";

export default function LandingPage({ lpMode = false, slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const navigate = useNavigate();
  const key = lpMode ? LP_PAGES[slug] : slug;
  const page = key ? LANDING_PAGES[key] : null;

  const faqSchema = page ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: page.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  } : null;

  useSEO(page ? {
    title: page.meta_title || `${page.keyword} | Mashara Finance`,
    description: page.meta_description || page.sub,
    path: lpMode ? `/lp/${slug}` : `/${slug}`,
    canonicalPath: `/${key}`,
    jsonLd: [
      faqSchema,
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: page.keyword, path: `/${key}` },
      ]),
    ].filter(Boolean),
  } : { title: "Page Not Found — Mashara Finance", path: lpMode ? `/lp/${slug}` : `/${slug}`, noindex: true });

  if (!page) {
    return (
      <div className="py-32 text-center" data-testid="lp-not-found">
        <h1 className="font-display text-3xl font-bold text-slate-900">Page not found</h1>
        <Link to="/" className="text-emerald-700 font-semibold mt-4 inline-block">Go home →</Link>
      </div>
    );
  }

  const mainModule = page.features[0];

  return (
    <div data-testid={`landing-${slug}`}>
      <ExitIntent />
      <section className="bg-gradient-to-b from-emerald-50/60 via-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16 sm:pt-24">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-4">{page.keyword} · Mashara Finance</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight text-slate-900" data-testid="lp-headline">{page.h1}</h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">{page.sub}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button data-testid="lp-trial-button" size="lg"
                onClick={() => { track("hero_cta_click", { location: `lp:${slug}` }); navigate("/signup"); }}
                className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white px-8 py-6 text-base font-semibold">
                Start Free Trial <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button data-testid="lp-demo-button" size="lg" variant="outline" onClick={() => navigate("/demo")}
                className="rounded-xl border-slate-300 px-8 py-6 text-base font-semibold text-slate-700 hover:text-emerald-700">
                Book a Demo
              </Button>
            </div>
            <p className="mt-4 text-sm text-slate-600 font-medium">10-day free trial · Activated with a ₹1 verification payment · Cancel anytime</p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-8">{page.problems_heading || "Sound Familiar?"}</h2>
            <div className="space-y-4">
              {page.problems.map((p) => (
                <div key={p} className="flex items-start gap-3 bg-red-50/60 border border-red-100 rounded-xl p-4">
                  <X className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700">{p}</span>
                </div>
              ))}
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-12 mb-8">{page.features_heading || "The Mashara Finance Way"}</h2>
            <div className="space-y-4">
              {page.features.map((f) => (
                <div key={f} className="flex items-start gap-3 bg-emerald-50/60 border border-emerald-100 rounded-xl p-4">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700">
                    <strong>{MODULE_LINKS[f] && MODULE_LINKS[f] !== `/${key}` ? <Link to={MODULE_LINKS[f]} className="hover:text-emerald-700">{MODULES[f].name}</Link> : MODULES[f].name}:</strong> {MODULES[f].benefit}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:sticky lg:top-24">
            <ProductMockup active={page.mockup || (mainModule === "multi_center" ? "dashboard" : mainModule)} alt={page.alt} />
            <div className="mt-6 bg-slate-900 text-white rounded-2xl p-6">
              <p className="font-display font-bold text-lg">Plans from ₹999/month</p>
              <p className="text-sm text-slate-300 mt-1">Transparent pricing · 10-day free trial · Cancel anytime</p>
              <div className="flex gap-3 mt-4">
                <Button data-testid="lp-pricing-trial-button" onClick={() => navigate("/signup")} className="rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white flex-1">Start Free Trial</Button>
                <Button data-testid="lp-pricing-view-button" variant="outline" onClick={() => navigate("/pricing")} className="rounded-xl border-slate-500 text-white bg-transparent hover:bg-white/10">View Pricing</Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 text-center mb-8">{page.faq_heading || "Frequently Asked Questions"}</h2>
          <Accordion type="single" collapsible>
            {page.faqs.map((f, i) => (
              <AccordionItem key={i} value={`lpfaq-${i}`}>
                <AccordionTrigger data-testid={`lp-faq-${i}`} className="text-left font-medium text-slate-900">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-10">
            <Button data-testid="lp-bottom-cta-button" size="lg" onClick={() => { track("hero_cta_click", { location: `lp_bottom:${slug}` }); navigate("/signup"); }}
              className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white px-8">
              Start Your 10-Day Free Trial
            </Button>
          </div>
          <div className="mt-10 pt-8 border-t border-slate-200 text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Related Pages</p>
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
              {(page.related || Object.entries(LANDING_PAGES).filter(([k]) => k !== key).slice(0, 4).map(([k, p]) => ({ label: p.keyword, to: `/${k}` }))).map((r) => (
                <Link key={r.to} to={r.to} data-testid={`lp-related-${r.to.split("/").pop()}`} className="text-sm font-medium text-emerald-700 hover:text-emerald-800">
                  {r.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
